import { adaNotificationActions, adaNotificationSelectors } from 'ducks/adaNotification'
import { authSelectors } from 'ducks/auth'
import { orgsActions, orgSelectors } from 'ducks/orgs'
import arrayMutators from 'final-form-arrays'
import { StyledDialog3, StyledDialogContent, StyledDialogTitle } from 'layout/StyledDialog'
import AdaIcon from 'layout/widgets/adaChat/AdaIcon'
import {
  Button,
  Chip,
  CircularProgress,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  DialogActions,
  Icon,
  IconButton,
  styled,
  Typography,
} from 'opensolar-ui'
import { PhoneInput } from 'pages/auth/components/auth3_0/elements/PhoneInput'
import { adaRegistrationService } from 'pages/auth/components/auth3_0/services/adaRegistrationService'
import {
  getFirstAvailablePlatform,
  getUsedPlatforms,
  renderCustomerReviewsFieldArray,
  renderSocialLinksFieldArray,
  SOCIAL_PLATFORM_CHOICES,
} from 'pages/auth/components/auth3_0/utils/adaBusinessFormComponents'
import {
  extractFormValuesFromAdaResponse,
  saveBusinessInfo,
} from 'pages/auth/components/auth3_0/utils/adaBusinessFormUtils'
import { BusinessRegistrationNumberInput } from 'resources/orgContent/ContentEdit'
import type { ExtractedFormValues } from 'types/orgs'
import { minLength } from 'ra-core'
import React, { useEffect, useState } from 'react'
import { ImageField, ImageInput, showNotification, TextInput, useTranslate } from 'react-admin'
import { Form } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { useDispatch, useSelector } from 'react-redux'
import appStorage from 'storage/appStorage'
import { OpenSolarPalette } from 'Themes'
import { osFetch } from 'util/fetch'

interface AdaReviewModalProps {
  open: boolean
  onClose: () => void
}

const AdaReviewModal: React.FC<AdaReviewModalProps> = ({ open, onClose }) => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const orgId = useSelector(authSelectors.getOrgId)
  const org = useSelector(orgSelectors.getOrg)
  const storedSessionId = useSelector(adaNotificationSelectors.getSessionId)
  const extractedDataFromRedux = useSelector(adaNotificationSelectors.getExtractedData)

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [hasAdaData, setHasAdaData] = useState(false)
  const [initialValues, setInitialValues] = useState<ExtractedFormValues>({})
  const [aiError, setAiError] = useState<string | null>(null)

  // Helper to check if a field should be shown (show all in manual mode, or only if AI found data)
  const shouldShowField = (fieldName: keyof ExtractedFormValues) => !hasAdaData || !!initialValues[fieldName]
  const shouldShowArrayField = (fieldName: keyof ExtractedFormValues) => {
    const value = initialValues[fieldName]
    return !hasAdaData || (Array.isArray(value) && value.length > 0)
  }

  useEffect(() => {
    if (!open) return

    const loadData = async () => {
      setIsLoading(true)
      setAiError(null)

      let dataToUse = extractedDataFromRedux

      // Fetch from backend if we don't have local data
      // This handles cross-browser login where localStorage is empty but backend has pending_review=true
      if (!dataToUse || dataToUse.length === 0) {
        try {
          const token = appStorage.getToken()
          if (token) {
            const response = await osFetch(`${window.API_ROOT}/api/ada/registration/pending-review/`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            })

            if (response.ok) {
              const data = await response.json()
              if (data.has_pending_review && data.extracted_data?.length > 0) {
                dataToUse = data.extracted_data
                dispatch(adaNotificationActions.setPendingWithData(data.session_id, data.extracted_data))
              }
            }
          }
        } catch (error) {
          console.warn('[AdaReviewModal] Failed to fetch from backend:', error)
        }
      }

      if (dataToUse && dataToUse.length > 0) {
        const successfulSteps = dataToUse.filter(
          (item: any) =>
            item.status !== 'error' && item.status !== 'failed' && (!item.errors || item.errors.length === 0)
        )

        const failedSteps = dataToUse.length - successfulSteps.length

        if (successfulSteps.length === 0) {
          setAiError('Failed to get response from AI. Please fill in the details manually.')
          setHasAdaData(false)
          setInitialValues({})
        } else {
          const response = {
            status: 'completed',
            session_id: storedSessionId || '',
            data: successfulSteps,
            step_statuses: {},
            result_urls: {},
            errors: [],
            warnings: [],
          }
          setHasAdaData(true)
          const values = extractFormValuesFromAdaResponse(response)
          setInitialValues(values)
          if (values.logo_url) {
            setLogoUrl(values.logo_url)
          }

          if (failedSteps > 0) {
            setAiError(`Some information couldn't be found automatically. Please fill in any missing details.`)
          } else {
            setAiError(null)
          }
        }
      } else {
        setAiError('Failed to get response from AI. Please fill in the details manually.')
        setHasAdaData(false)
        setInitialValues({})
      }

      setIsLoading(false)
    }

    loadData()
  }, [open, storedSessionId, extractedDataFromRedux, dispatch])

  const handleSave = async (formValues: Record<string, any>) => {
    if (!orgId) {
      dispatch(showNotification('Organization not found', 'warning'))
      return
    }

    setIsSaving(true)
    try {
      if (storedSessionId) {
        adaRegistrationService.setSessionId(storedSessionId)
      }

      const result = await saveBusinessInfo({
        orgId,
        formValues,
        logoUrl,
        initialAiValues: initialValues,
        includeOrgIdentityFields: false,
        existingOrgLogoUrl: org?.logo_public_url ? String(org.logo_public_url) : null,
      })

      if (!result.success) {
        dispatch(showNotification(result.error || 'Failed to save business information', 'warning'))
        return
      }

      dispatch(adaNotificationActions.clear())
      dispatch(orgsActions.reloadOrg())

      if (result.warning) {
        dispatch(showNotification(`Business information saved. Note: ${result.warning}`, 'warning'))
      } else {
        dispatch(showNotification('Business information saved successfully', 'info'))
      }
      onClose()
    } catch (error: any) {
      dispatch(showNotification(error?.message || 'Failed to save business information', 'warning'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    onClose()
  }

  if (!open) return null

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <StyledDialog3 open={open} maxWidth="md" onClose={handleCancel}>
        <StyledDialogTitle onClose={handleCancel}>
          <TitleContainer>
            <span>{translate(aiError || !hasAdaData ? 'Tell us about your business' : "Here's what we found")}</span>
            {!aiError && hasAdaData && (
              <AdaBadge
                label={
                  <BadgeContent>
                    <AdaIcon size={12} />
                    Ada
                  </BadgeContent>
                }
                variant="outlined"
                size="small"
              />
            )}
          </TitleContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          {isLoading ? (
            <LoadingContainer>
              <CircularProgress size={40} />
              <Typography textVariant="body1">{translate('Loading your business information...')}</Typography>
            </LoadingContainer>
          ) : (
            <Form
              initialValues={initialValues}
              mutators={{ ...arrayMutators }}
              onSubmit={handleSave}
              render={({ handleSubmit, form, values }) => (
                <form onSubmit={handleSubmit}>
                  <DescriptionText>
                    {translate(
                      aiError
                        ? aiError
                        : hasAdaData
                        ? 'Ada was able to find these information for your business. Just check it over and tweak anything if needed.'
                        : "Ada couldn't find information for your business. You can fill in the details manually below."
                    )}
                  </DescriptionText>

                  {(shouldShowArrayField('business_reg_numbers') || shouldShowField('logo_url')) && (
                    <Section>
                      <SectionTitle>{translate('Business Identity')}</SectionTitle>

                      {shouldShowArrayField('business_reg_numbers') && (
                        <BusinessRegistrationNumberInput
                          businessRegNumbers={values?.business_reg_numbers ?? []}
                          typeFilter="gs_tax_number"
                          limit={1}
                        />
                      )}

                      {shouldShowField('logo_url') && (
                        <div>
                          <Typography textVariant="body2" style={{ marginBottom: 8 }}>
                            {translate('Business Logo')}
                          </Typography>
                          {(() => {
                            const logo = values?.logo
                            const uploadedLogoSrc = logo ? (Array.isArray(logo) ? logo[0]?.src : logo?.src) : null
                            const hasUploadedLogo = !!uploadedLogoSrc

                            return (
                              <>
                                {logoUrl && !hasUploadedLogo && (
                                  <LogoPreviewContainer>
                                    <LogoImage src={logoUrl} alt="Business Logo" />
                                    <LogoActions>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setLogoUrl(null)
                                          form.change('logo_url', null)
                                        }}
                                      >
                                        <Icon icon="close" size="small" />
                                      </IconButton>
                                    </LogoActions>
                                  </LogoPreviewContainer>
                                )}
                                {hasUploadedLogo && (
                                  <LogoPreviewContainer>
                                    <LogoImage src={uploadedLogoSrc} alt="Business Logo" />
                                    <LogoActions>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          form.change('logo', null)
                                        }}
                                      >
                                        <Icon icon="close" size="small" />
                                      </IconButton>
                                    </LogoActions>
                                  </LogoPreviewContainer>
                                )}
                                {!logoUrl && !hasUploadedLogo && (
                                  <ImageInput
                                    source="logo"
                                    label={null}
                                    accept="image/*"
                                    placeholder={
                                      <LogoUploadPlaceholder>
                                        <Icon icon="cloud_upload" size="medium" color={OpenSolarPalette.greyMid1} />
                                        <Typography
                                          textVariant="caption1"
                                          colorHex={OpenSolarPalette.greyMid1 as `#${string}`}
                                        >
                                          {translate('Drag & drop or click to upload')}
                                        </Typography>
                                      </LogoUploadPlaceholder>
                                    }
                                  >
                                    <ImageField source="src" title="title" />
                                  </ImageInput>
                                )}
                              </>
                            )
                          })()}
                        </div>
                      )}
                    </Section>
                  )}

                  {(shouldShowField('zip') ||
                    shouldShowField('state') ||
                    shouldShowField('locality') ||
                    shouldShowField('address') ||
                    shouldShowField('sales_phone_number') ||
                    shouldShowArrayField('social_links')) && (
                    <Section>
                      <SectionTitle>{translate('Contact Details')}</SectionTitle>

                      {shouldShowField('zip') && (
                        <TextInput label={translate('Postcode/ZIP')} source="zip" fullWidth variant="outlined" />
                      )}

                      {(shouldShowField('state') || shouldShowField('locality')) && (
                        <RowFields>
                          {shouldShowField('state') && (
                            <TextInput label={translate('State/Region')} source="state" fullWidth variant="outlined" />
                          )}
                          {shouldShowField('locality') && (
                            <TextInput
                              label={translate('City/Locality')}
                              source="locality"
                              fullWidth
                              variant="outlined"
                            />
                          )}
                        </RowFields>
                      )}

                      {shouldShowField('address') && (
                        <TextInput label={translate('Street Address')} source="address" fullWidth variant="outlined" />
                      )}

                      {shouldShowField('sales_phone_number') && (
                        <PhoneInput
                          source="sales_phone_number"
                          fullWidth
                          label={translate('Business Phone Number')}
                          validate={[minLength(4)]}
                          initialCountry={org?.country?.iso2 || org?.country_iso2}
                        />
                      )}

                      {shouldShowArrayField('social_links') && (
                        <div>
                          <Typography textVariant="body2" style={{ marginBottom: 8 }}>
                            {translate('Social Media')}
                          </Typography>
                          <FieldArray name="social_links">
                            {({ fields }) => {
                              const usedPlatforms = getUsedPlatforms(fields, -1)
                              const allPlatformsUsed = usedPlatforms.length >= SOCIAL_PLATFORM_CHOICES.length
                              const nextPlatform = getFirstAvailablePlatform(usedPlatforms)
                              return (
                                <FieldArrayContainer>
                                  {renderSocialLinksFieldArray({ fields, translate })}
                                  <AddButton
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => fields.push({ url: '', platform: nextPlatform })}
                                    disabled={allPlatformsUsed}
                                  >
                                    + {translate('Add Social Link')}
                                  </AddButton>
                                </FieldArrayContainer>
                              )
                            }}
                          </FieldArray>
                        </div>
                      )}
                    </Section>
                  )}

                  {(shouldShowField('about_content') ||
                    shouldShowField('why_choose_us') ||
                    shouldShowArrayField('customer_reviews')) && (
                    <Section>
                      <SectionTitle>{translate('Business Profile')}</SectionTitle>

                      {shouldShowField('about_content') && (
                        <TextInput
                          label={translate('About Us')}
                          source="about_content"
                          fullWidth
                          variant="outlined"
                          multiline
                          rows={4}
                        />
                      )}

                      {shouldShowField('why_choose_us') && (
                        <TextInput
                          label={translate('Why Choose Us?')}
                          source="why_choose_us"
                          fullWidth
                          variant="outlined"
                          multiline
                          rows={4}
                        />
                      )}

                      {shouldShowArrayField('customer_reviews') && (
                        <div>
                          <Typography textVariant="body2" style={{ marginBottom: 8 }}>
                            {translate('Customer Reviews')}
                          </Typography>
                          <FieldArray name="customer_reviews">
                            {({ fields }) => (
                              <FieldArrayContainer>
                                {renderCustomerReviewsFieldArray({ fields, translate })}
                                <AddButton variant="outlined" fullWidth onClick={() => fields.push({ text: '' })}>
                                  + {translate('Add Review')}
                                </AddButton>
                              </FieldArrayContainer>
                            )}
                          </FieldArray>
                        </div>
                      )}
                    </Section>
                  )}

                  <StyledDialogActions>
                    <Button variant="contained" color="default" onClick={handleCancel} disabled={isSaving}>
                      {translate('Cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={isSaving}
                      startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
                    >
                      {translate('Save Changes')}
                    </Button>
                  </StyledDialogActions>
                </form>
              )}
            />
          )}
        </StyledDialogContent>
      </StyledDialog3>
    </ComponentVersionsInherit>
  )
}

export default AdaReviewModal

const TitleContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

const AdaBadge = styled(Chip)({
  borderColor: OpenSolarPalette.openSolarYellow,
  backgroundColor: OpenSolarPalette.themeColorLight,
  '& .MuiChip-label': {
    padding: '2px 6px',
  },
})

const BadgeContent = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
})

const DescriptionText = styled(Typography)({
  marginBottom: 24,
  color: OpenSolarPalette.greyMid1,
})

const Section = styled('div')({
  marginBottom: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
})

const SectionTitle = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 8,
  color: OpenSolarPalette.greyMid1,
})

const RowFields = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
})

const LoadingContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  minHeight: 200,
  padding: 40,
})

const LogoUploadPlaceholder = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: 24,
  border: `2px dashed ${OpenSolarPalette.greyLight2}`,
  borderRadius: 8,
  cursor: 'pointer',
})

const LogoPreviewContainer = styled('div')({
  position: 'relative',
  display: 'inline-block',
  padding: 16,
  border: `1px solid ${OpenSolarPalette.greyLight2}`,
  borderRadius: 8,
  backgroundColor: '#fafafa',
})

const LogoImage = styled('img')({
  maxWidth: 200,
  maxHeight: 100,
  objectFit: 'contain',
})

const LogoActions = styled('div')({
  position: 'absolute',
  top: 4,
  right: 4,
})

const FieldArrayContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const AddButton = styled(Button)({
  borderStyle: 'dashed',
  marginTop: 8,
})

const StyledDialogActions = styled(DialogActions)({
  padding: '16px 0',
  marginTop: 16,
  borderTop: `1px solid ${OpenSolarPalette.greyLight2}`,
})
